import {
  derived,
  get,
  type Readable,
  readable,
  type Subscriber,
  type Unsubscriber,
  type Writable,
  writable,
} from "svelte/store";
import { DateModel } from "$models";
import { err, ok, type Result } from "./result.ts";
import { checkLuhn } from "../utils/validation.ts";

export { err, ok, type Result };
export type State<output, e = string> = {
  hasChanged: boolean;
  res: Result<output, e>;
};
export type FormState<O, E = string> = State<O, E>;

export type FieldError = { field: string | null; error: string };
export type ValidationFn<input, output> = (v: input) => Result<output, string>;

export type FormOutput<Out> = Readable<State<Out, string>>;

export interface Formable<Out> {
  output: FormOutput<Out>;
}

export class FormInput<In> {
  input: Writable<State<In, string>>;
  output: Readable<State<In, string>>;
  constructor(validator?: Validator<In, In>, initial?: In) {
    this.input = writable({
      hasChanged: false,
      res: validator
        ? validator.fn(initial!)
        : initial
        ? ok(initial)
        : err("Värdet saknas. Programmeraren har gjort fel"),
    });
    this.output = derived(
      this.input,
      (input) =>
        validator && input.res.isOk()
          ? {
            hasChanged: input.hasChanged,
            res: validator.fn(input.res.value),
          }
          : input,
    );
  }

  set(v: State<In, string>) {
    this.input.set(v);
  }
  setValue(v: In) {
    const prevValue = get(this.input).res.optional();
    let hasChanged = false;
    if (JSON.stringify(prevValue) !== JSON.stringify(v)) {
      hasChanged = true;
    }
    this.input.set({ hasChanged, res: ok(v) });
  }
  setError(error: string) {
    this.input.set({ hasChanged: true, res: err(error) });
  }
  subscribeTo(store: Readable<State<In, string>>): () => void {
    return store.subscribe((s) => this.input.set(s));
  }
  subscribe(
    sub: (value: State<In, string>) => void,
  ): () => void {
    return this.output.subscribe(sub);
  }

  public mapValue<NewType>(fn: (n: In) => NewType): MappedForm<In, NewType> {
    const resMap = (resIn: Result<In, string>): Result<NewType, string> => {
      if (resIn.isOk()) {
        return ok(fn(resIn.value));
      } else {
        return err(resIn.error);
      }
    };
    return new MappedForm(this.input, resMap);
  }

  public flatMapValue<NewType>(
    fn: (n: In) => Result<NewType, string>,
  ): MappedForm<In, NewType> {
    const resMap = (resIn: Result<In, string>): Result<NewType, string> => {
      if (resIn.isOk()) {
        return fn(resIn.value);
      } else {
        return err(resIn.error);
      }
    };
    return new MappedForm(this.input, resMap);
  }

  // TODO write mapValue that only maps when ok to an ok
  public mapRes<NewType>(
    fn: (n: Result<In, string>) => Result<NewType, string>,
  ): MappedForm<In, NewType> {
    return new MappedForm(this.input, fn);
  }
}

class MappedForm<In, Out> {
  public output: Readable<State<Out, string>>;
  constructor(
    input: Readable<State<In, string>>,
    mapper: (n: Result<In, string>) => Result<Out, string>,
  ) {
    this.output = derived(input, (input) => {
      return {
        ...input,
        res: mapper(input.res),
      };
    });
  }

  public mapValue<NewType>(fn: (n: Out) => NewType): MappedForm<Out, NewType> {
    const resMap = (resIn: Result<Out, string>): Result<NewType, string> => {
      if (resIn.isOk()) {
        return ok(fn(resIn.value));
      } else {
        return err(resIn.error);
      }
    };
    return new MappedForm(this.output, resMap);
  }

  public flatMapValue<NewType>(
    fn: (n: Out) => Result<NewType, string>,
  ): MappedForm<Out, NewType> {
    const resMap = (resIn: Result<Out, string>): Result<NewType, string> => {
      if (resIn.isOk()) {
        return fn(resIn.value);
      } else {
        return err(resIn.error);
      }
    };
    return new MappedForm(this.output, resMap);
  }

  // TODO write mapValue that only maps when ok to an ok
  public mapRes<NewType>(
    fn: (n: Result<Out, string>) => Result<NewType, string>,
  ): MappedForm<Out, NewType> {
    return new MappedForm(this.output, fn);
  }
}

export class Validator<In, Out> {
  constructor(public fn: (v: In) => Result<Out, string>) {}

  and<NewOut>(
    other: Validator<Out, NewOut>,
  ): Validator<In, NewOut> {
    return new Validator((input: In) => {
      const res = this.fn(input);
      return res.isOk() ? other.fn(res.value) : err(res.error);
    });
  }

  map<NewOut>(fn: (v: Out) => NewOut): Validator<In, NewOut> {
    return new Validator((input: In) => {
      const res = this.fn(input);
      return res.isOk() ? ok(fn(res.value)) : err(res.error);
    });
  }
}

interface FormableObject<output> {
  [thingName: string]: Formable<output>;
}

// deno-lint-ignore no-namespace
export namespace Form {
  export function fixed<output>(
    output: output,
  ): Formable<output> {
    return { output: readable({ hasChanged: false, res: ok(output) }) };
  }

  export function fromStore<In, Out = In>(
    store: Readable<In>,
    mapper?: (state: In) => Result<Out, string>,
  ): Formable<Out> {
    const identityMapper = (s: In) => ok<Out, string>(s as unknown as Out);
    const map = mapper ?? identityMapper;
    const output = derived(store, (state) => ({
      hasChanged: false,
      res: map(state),
    }));
    return { output };
  }

  export function map<From, To>(
    form: Formable<From>,
    mapTo: (val: From) => State<To, string>,
  ): Formable<To> {
    const output = derived(form.output, (state) => {
      if (state.res.isOk()) {
        return mapTo(state.res.value);
      } else {
        return {
          ...state,
          res: err<To, string>(state.res.error),
        };
      }
    });
    return { output };
  }

  export function flatMap<From, To>(
    form: Formable<From>,
    mapTo: (val: From) => To,
  ): Formable<To> {
    const output = derived(form.output, (state) => {
      if (state.res.isOk()) {
        return { ...state, res: ok<To, string>(mapTo(state.res.value)) };
      } else {
        return {
          ...state,
          res: err<To, string>(state.res.error),
        };
      }
    });
    return { output };
  }

  export function firstOk<Out>(
    ...fields: Formable<Out>[]
  ): Formable<Out> {
    const stores = fields.map((f) => f.output);
    const output = derived(stores, (stores) => {
      return stores.reduce((prev, cur) => {
        if (prev.res.isOk()) {
          return prev;
        }

        // doesnt matter if it is ok or not
        // we always want the first ok value
        // or the last error value
        return cur;
      }, {
        hasChanged: false,
        res: err("firstOk: Något av dessa fält måste fyllas i"),
      });
    });
    return { output };
  }

  export function oneOf<output>(
    decider: Formable<keyof Required<typeof fields>>,
    fields: FormableObject<output>,
  ): Readable<State<output, string>> {
    const stores = [];
    const fieldKeys: Array<keyof typeof fields> = [];

    for (const key in fields) {
      stores.push(fields[key].output);
      fieldKeys.push(key);
    }

    const output: Readable<State<output, string>> = derived(
      [decider.output, ...stores],
      ([deciderStore, ...stores], set) => {
        if (deciderStore.res.isOk()) {
          const chosenField = deciderStore.res.value;
          const selectedStoreIndex = fieldKeys.findIndex((v) =>
            v === chosenField
          );

          if (selectedStoreIndex === -1) return;

          const selectedStore = stores[selectedStoreIndex];

          set({
            hasChanged: selectedStore.hasChanged || deciderStore.hasChanged,
            res: selectedStore.res,
          });
        }
      },
      { hasChanged: false as boolean, res: err("") as Result<output, string> },
    );

    return output;
  }

  export class Field<Out> {
    input: Writable<string>;
    output: Readable<State<Out, string>>;
    initialValue: Writable<string>;
    DOMElement: Node | undefined;

    constructor(
      validator: Validator<string, Out>,
      initial: string | Formable<string>,
    ) {
      this.initialValue = writable(
        isFormable(initial) ? get(initial.output).res.optional() : initial,
      );
      const state: State<Out, string> = {
        hasChanged: false,
        res: validator.fn(get(this.initialValue) ?? ""),
      };

      this.input = writable(get(this.initialValue));
      this.output = derived(
        [this.input, this.initialValue],
        ([input, initialValue], set) => {
          const hasChanged = input !== initialValue;
          const res = validator.fn(input);
          set({ hasChanged, res });
        },
        state,
      );

      if (isFormable(initial)) {
        initial.output.subscribe((init) =>
          this.input.set(init.res.optional() ?? "")
        );
      }
    }

    subscribe(
      subscription: (value: State<Out, string>) => void,
    ): () => void {
      return this.output.subscribe(subscription);
    }

    or<otherOutput>(
      other: Formable<otherOutput>,
    ): Formable<Out | otherOutput> {
      const output: Readable<State<Out | otherOutput, string>> = derived([
        this.output,
        other.output,
      ], (states, set) => {
        const hasChanged = states.find((state) =>
          state.hasChanged
        ) !== undefined;
        const firstOk = states.find((state) => state.res.isOk());
        const firstErr = states.find((state) => !state.res.isOk());

        if (firstOk && firstOk.res.isOk()) {
          set({ hasChanged, res: firstOk.res });
        } else if (firstErr && !firstErr.res.isOk()) {
          set({ hasChanged, res: firstErr.res });
        }
      });

      return {
        output,
      };
    }

    public mapValue<NewOut>(fn: (n: Out) => NewOut): MappedForm<Out, NewOut> {
      const resMap = (resIn: Result<Out, string>): Result<NewOut, string> => {
        if (resIn.isOk()) {
          return ok(fn(resIn.value));
        } else {
          return err(resIn.error);
        }
      };
      return new MappedForm(this.output, resMap);
    }

    public resetHasChanged() {
      this.initialValue.set(get(this.input));
    }
  }

  export function input<In>(
    validator?: Validator<In, In>,
    initial?: In,
  ): FormInput<In> {
    return new FormInput(validator, initial);
  }

  export function field<Out>(
    validator: Validator<string, Out>,
    initialValue?: string | Formable<string> | null,
  ): Field<Out> {
    return new Field(validator, initialValue ?? "");
  }

  export class Checkbox<output> {
    input: Writable<boolean>;
    output: Readable<State<output, string>>;

    constructor(
      validator: Validator<boolean, output>,
      initialValue: boolean = false,
    ) {
      // First value is what we put in the input store.
      const firstValue = initialValue ? initialValue : false;
      /*const state: State<output, string> = {
        hasChanged: false,
        res: validator.fn(firstValue),
      };*/

      this.input = writable(firstValue);
      this.output = derived(
        this.input,
        (input) => {
          const hasChanged = input !== firstValue;
          const res = validator.fn(input);
          return { hasChanged, res };
        },
      );
    }

    subscribe(
      subscription: (value: State<output, string>) => void,
    ): () => void {
      return this.output.subscribe(subscription);
    }
  }

  export function checkbox<output>(
    validator: Validator<boolean, output>,
    initialValue?: boolean,
  ): Checkbox<output> {
    return new Checkbox(validator, initialValue);
  }

  export class Toggle<output> {
    input: Writable<boolean>;
    output: Readable<State<output | undefined, string>>;

    constructor(
      field: Formable<output>,
      initialValue?: output,
    ) {
      // First value is what we put in the input store.
      const firstState = initialValue ? initialValue : undefined;
      const firstInput = initialValue ? true : false;

      const state: State<output | undefined, string> = {
        hasChanged: false,
        res: ok(firstState),
      };

      this.input = writable(firstInput);

      this.output = derived(
        [this.input, field.output],
        ([input, field], set) => {
          const hasChanged = input !== firstInput;
          if (input) {
            set({ hasChanged: hasChanged || field.hasChanged, res: field.res });
          } else {
            set({
              hasChanged: hasChanged || field.hasChanged,
              res: ok(undefined),
            });
          }
        },
        state,
      );
    }

    subscribe(
      subscription: (value: State<output | undefined, string>) => void,
    ): () => void {
      return this.output.subscribe(subscription);
    }
  }

  export function toggle<output>(
    field: Formable<output>,
    initialValue?: output,
  ): Toggle<output> {
    return new Toggle(field, initialValue);
  }

  export class List<Out, Formz extends Formable<Out>> implements Iterable<Out> {
    inputs: Formz[];
    indexes: Writable<number[]>;
    output: Writable<State<Out[], string>>;
    outputUnsub: Unsubscriber;
    public subscribe: (
      this: void,
      run: Subscriber<State<Out[], string>>,
    ) => Unsubscriber;

    constructor(initial: Out[] | Formz[]) {
      if (initial.length === 0) {
        this.inputs = [];
      } else if (isFormable(initial[0])) {
        this.inputs = initial as Formz[];
      } else {
        this.inputs = (initial as Out[]).map((val: Out) =>
          new FormInput(undefined, val) as unknown as Formz // This is a bad bad hack. Instead of doing this the Formz type should extend from something with a constructor
        );
      }

      this.indexes = writable(this.inputs.map((_, i) => i));
      this.output = writable({
        hasChanged: false,
        res: err("Form.List: not yet initiated"),
      });
      this.outputUnsub = () => {};
      this.recalculateOutput();

      this.subscribe = this.output.subscribe;
    }

    recalculateOutput() {
      this.indexes.set(this.inputs.map((_, i) => i));
      const aggregate = derived(this.inputs.map((i) => i.output), (inputs) => {
        const out = inputs.reduce<State<Out[], string>>((prev, cur) => {
          if (prev.res.isOk() && cur.res.isOk()) {
            return {
              hasChanged: prev.hasChanged || cur.hasChanged,
              res: ok<Out[], string>([...prev.res.value, cur.res.value]),
            };
          } else if (!prev.res.isOk()) {
            return prev;
          }
          return {
            hasChanged: cur.hasChanged || prev.hasChanged,
            res: cur.res.map((v) => [v]),
          };
        }, { hasChanged: false, res: ok([]) } as State<Out[], string>);
        return out;
      });
      this.outputUnsub();
      this.outputUnsub = aggregate.subscribe((aggregate) => {
        this.output.set(aggregate);
      });
    }

    public addForm(item: Formz) {
      this.inputs = [...this.inputs, item];
      this.recalculateOutput();
    }
    public remove(index: number) {
      this.inputs = this.inputs.filter((_, i) => i !== index);
      this.recalculateOutput();
    }
    public get(index: number): Formz {
      return this.inputs[index];
    }

    [Symbol.iterator](): Iterator<Out> {
      const iter = get(this.output).res;
      if (iter.isOk()) {
        return iter.value[Symbol.iterator]();
      } else {
        return [][Symbol.iterator]();
      }
    }

    public count(): number {
      return this.inputs.length;
    }
  }

  function isStore<T>(val: T | Readable<T>): val is Readable<T> {
    return typeof val === "object" && val !== null && "subscribe" in val;
  }
  function isFormable<T>(val: T | Formable<T>): val is Formable<T> {
    return typeof val === "object" && val !== null && "output" in val &&
      isStore(val.output);
  }

  export class MultiSelect<output> {
    input: Writable<string | number>;
    output: Readable<State<output, string>>;
    choices: Readable<SelectChoice<output>[]>;

    constructor(
      choices: SelectChoice<output>[] | Readable<SelectChoice<output>[]>,
      initialValue?: SelectChoice<output>,
    ) {
      this.choices = isStore(choices) ? choices : writable(choices);
      // First value is what we put in the input store.

      const init: SelectChoice<output> | undefined = initialValue ??
        get(this.choices)[0];
      const firstLabel = init?.label;

      this.input = writable(firstLabel);

      const state: State<output, string> = firstLabel === undefined
        ? {
          hasChanged: false,
          res: err("Systemfel: Inget val kan göras på en tom lista."),
        }
        : {
          hasChanged: false,
          res: ok(init.value),
        };

      this.output = derived(
        [this.input, this.choices],
        ([input, choices], set) => {
          const hasChanged = input !== firstLabel;
          const found = choices.find((thing) => thing.label === input);
          if (found) {
            set({ hasChanged, res: ok(found.value) });
          } else {
            set({
              hasChanged,
              res: err("på nått vänster valde vi något ogiltigt"),
            });
          }
        },
        state,
      );
    }

    subscribe(
      subscription: (value: State<output, string>) => void,
    ): () => void {
      return this.output.subscribe(subscription);
    }

    map<newoutput>(mapFn: (value: output) => newoutput): Formable<newoutput> {
      return {
        output: derived(this.output, (input, set) => {
          if (input.res.isOk()) {
            set({
              hasChanged: input.hasChanged,
              res: ok(mapFn(input.res.value)),
            });
          } else {
            set({ hasChanged: input.hasChanged, res: err(input.res.error) });
          }
        }),
      };
    }

    isOneOf(...values: output[]): Readable<boolean> {
      return derived(this.output, (output) => {
        return output
          .res
          .map((r) => values.includes(r))
          .unwrapOr(false);
      });
    }

    setValue(value: output) {
      const choice = get(this.choices).find((choice) => choice.value === value);
      this.input.set(choice?.label ?? get(this.input));
    }
  }

  export interface SelectChoice<ValueKind> {
    value: ValueKind;
    label: string;
    disabled?: boolean;
  }

  export function multiSelect<output>(
    choices: SelectChoice<output>[] | Readable<SelectChoice<output>[]>,
    initialValue?: SelectChoice<output>,
  ): MultiSelect<output> {
    return new MultiSelect(choices, initialValue);
  }

  export function choicesFromEnum<Enum extends object>(
    enm: Enum,
    labelFunc: (variant: ValuesOf<Enum>) => string,
    disabled: ValuesOf<Enum>[] = [],
  ): SelectChoice<ValuesOf<Enum>>[] {
    return Object.values(enm).map((variant) => ({
      value: variant,
      label: labelFunc(variant),
      disabled: disabled.includes(variant),
    }));
  }

  type ValuesOf<T> = T[keyof T];

  export function allOrNothing<Output>(
    obj: { [field in keyof Required<Output>]: Formable<Output[field] | null> },
  ): Formable<Output | null> {
    // A map of indexes to know what store to map against what field.
    const stores: FormOutput<ValuesOf<Output> | null>[] = [];
    const fieldKeys: Array<keyof Output> = [];

    // Push the stores to an array for use in derived
    // Push the field keys of obj in the same order.
    for (const key in obj) {
      stores.push(obj[key].output);
      fieldKeys.push(key);
    }

    const output: FormOutput<Output | null> = derived(
      stores,
      ([...stores]: State<ValuesOf<Output> | null, string>[]) => {
        const result: Partial<Output> = {};
        let changed = false;

        for (let i = 0; i < stores.length; i++) {
          const fieldState = stores[i];
          const fieldKey = fieldKeys[i];

          changed = fieldState.hasChanged || changed;

          if (fieldState.res.isOk()) {
            if (
              fieldState.res.value === null ||
              fieldState.res.value === undefined || fieldState.res.value === ""
            ) {
              return {
                hasChanged: changed,
                res: ok<Output | null, string>(null),
              };
            }
            result[fieldKey] = fieldState.res.value;
          } else {
            return {
              hasChanged: changed,
              res: err<Output | null, string>(fieldState.res.error),
            };
          }
        }
        return {
          hasChanged: changed,
          res: ok<Output | null, string>(result as Output),
        };
      },
    );
    return { output };
  }

  export function object<output>(
    obj: { [field in keyof Required<output>]: Formable<output[field]> },
  ): Readable<State<output, string>> {
    // A map of indexes to know what store to map against what field.
    const stores = [];
    const fieldKeys: Array<keyof output> = [];

    // Push the stores to an array for use in derived
    // Push the field keys of obj in the same order.
    for (const key in obj) {
      stores.push(obj[key].output);
      fieldKeys.push(key);
    }

    return derived(stores, (stores, set) => {
      const result: any = {};
      let changed = false;

      for (let i = 0; i < stores.length; i++) {
        const fieldState = stores[i];
        const fieldKey = fieldKeys[i];

        changed = fieldState.hasChanged || changed;

        if (fieldState.res.isOk()) {
          result[fieldKey] = fieldState.res.value;
        } else {
          set({ hasChanged: changed, res: err(fieldState.res.error) });
          return;
        }
      }
      set({ hasChanged: changed, res: ok(result) });
    });
  }

  export function list<Out, Formz extends Formable<Out>>(
    initialValue?: Out[] | Formz[],
  ): List<Out, Formz> {
    return new List<Out, Formz>(initialValue ?? []);
  }

  export function nest<output>(
    form: Readable<State<output, string>>,
  ): Formable<output> {
    return { output: form };
  }
}

// deno-lint-ignore no-namespace
export namespace Validate {
  export function any(): Validator<string, string> {
    return new Validator((s) => ok(s));
  }

  export function alphaNumeric(
    errMsg: string = "Detta fält får bara innehålla bokstäver och siffror",
  ): Validator<string, string> {
    return new Validator((s) => {
      if (s.match(/^[a-zA-Z1-9åäö]*$/)) {
        return ok(s);
      } else {
        return err(errMsg);
      }
    });
  }

  export function mustEqual<T>(val: T, msg: string): Validator<T, T> {
    return new Validator((s) => {
      if (s === val) {
        return ok(s);
      } else {
        return err(msg ?? `Måste vara "${val}"`);
      }
    });
  }

  export function len(
    min?: number,
    max?: number,
    minMsg?: string,
    maxMsg?: string,
  ): Validator<string, string> {
    return new Validator((s) => {
      if (min && s.length < min) {
        return err(minMsg ?? `Måste vara minst ${min} tecken långt`);
      } else if (max && max < s.length) {
        return err(maxMsg ?? `Får vara max ${max} tecken långt`);
      } else {
        return ok(s);
      }
    });
  }

  export function arrayLen<T>(
    min?: number,
    max?: number,
    minMsg?: string,
    maxMsg?: string,
  ): Validator<T[], T[]> {
    return new Validator((s) => {
      if (min && s.length < min) {
        return err(minMsg ?? `Måste vara minst ${min} lång`);
      } else if (max && max < s.length) {
        return err(maxMsg ?? `Får vara max ${max} lång`);
      } else {
        return ok(s);
      }
    });
  }

  export function minItems<In>(
    min: number,
    error: string,
  ): Validator<In[], In[]> {
    return new Validator<In[], In[]>((s) => {
      if (s.length < min) {
        return err(error);
      } else {
        return ok(s);
      }
    });
  }

  export function required(
    msg: string = "Måste anges",
  ): Validator<string | null | undefined, string> {
    return new Validator((s) => {
      if (s === null || s === undefined) {
        return err(msg);
      }

      if (s.length === 0) {
        return err(msg);
      } else {
        return ok(s);
      }
    });
  }

  // Note: Used for checkbox form type.
  export function boolean(): Validator<boolean, boolean> {
    return new Validator((b: boolean) => {
      if (typeof b !== "boolean") {
        return err("Måste vara sant eller falskt");
      } else {
        return ok(b);
      }
    });
  }

  export function integer(
    radix?: number,
    msg: string = "Måste vara ett giltigt heltal",
  ): Validator<string, number> {
    return new Validator((s: string) => {
      if (/^-?\d+$/.test(s)) {
        const n = Number.parseInt(s, radix);
        if (!Number.isNaN(n) && n.toString() == s) {
          return ok(n);
        }
      }
      return err(msg);
    });
  }

  export function float(): Validator<string | number | null, number> {
    return new Validator((s: string | number | null) => {
      if (typeof s === "number") {
        return ok(s);
      }

      if (s === null) {
        return err("Måste vara ett giligit decimaltal");
      }

      if (s.includes(",")) {
        return err("Använd punkt som decimalskiljetecken");
      }
      const f = Number.parseFloat(s);
      if (Number.isNaN(f)) {
        return err("Måste vara ett giltigt decimaltal");
      } else {
        return ok(f);
      }
    });
  }

  export function email(): Validator<string, string> {
    return new Validator((s) => {
      const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+(\.[a-zA-Z]{2,})?$/;
      if (!regex.test(s)) {
        return err("Är inte en giltig e-postadress");
      } else {
        return ok(s);
      }
    });
  }

  export function array(): Validator<string, any[]> {
    return new Validator((a) => {
      if (!Array.isArray(a)) {
        return err("Måste vara en giltig array");
      } else {
        return ok(a);
      }
    });
  }

  export function posLat(): Validator<string, number> {
    return new Validator((s: string) => {
      const f = Number.parseFloat(s);

      if (!isFinite(f) || 90 < Math.abs(f)) {
        return err("ogiltig latitud");
      }

      if (f < 55 || 70 < f) {
        return err("inte i Sverige(55 - 70)");
      }

      return ok(f);
    });
  }

  export function posLng(): Validator<string, number> {
    return new Validator((s: string) => {
      const f = Number.parseFloat(s);

      if (!isFinite(f) || 180 < Math.abs(f)) {
        return err("ogiltig longitud");
      }

      if (f < 10 || 25 < f) {
        return err("inte i Sverige(10 - 25)");
      }

      return ok(f);
    });
  }

  export function date(): Validator<string, DateModel> {
    return new Validator((s: string) => {
      try {
        const dateModel = new DateModel(s);
        return ok(dateModel);
      } catch {
        return err("Inte ett korrekt datum");
      }
    });
  }

  /// inclusice range
  export function dateInRange({ min, max, msgMin, msgMax }: {
    min?: Date;
    max?: Date;
    msgMin?: string;
    msgMax?: string;
  }): Validator<DateModel, DateModel> {
    return new Validator((date: DateModel) => {
      if (min && date.asDate() < min) {
        return err(msgMin ?? "Datumet får inte vara mindra än " + max);
      } else if (max && date.asDate() > max) {
        return err(msgMin ?? "Datumet får inte vara större än " + max);
      } else {
        return ok(date);
      }
    });
  }

  export function numInRange({ min, max, msgMin, msgMax }: {
    min?: number;
    max?: number;
    msgMin?: string;
    msgMax?: string;
  }): Validator<number, number> {
    return new Validator<number, number>((num: number) => {
      if (min && num < min) {
        return err<number, string>(msgMin ?? "Får inte vara färre än " + max);
      } else if (max && num > max) {
        return err<number, string>(msgMin ?? "Får inte vara fler än " + max);
      } else {
        return ok<number, string>(num);
      }
    });
  }

  export function uuid(
    errMsg = "Är inte ett giltigt UUID",
  ): Validator<string, string> {
    return new Validator((s) => {
      const regex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!regex.test(s)) {
        return err(errMsg);
      } else {
        return ok(s);
      }
    });
  }

  export function validBankgiroNumber(): Validator<string, string> {
    return new Validator((s) => {
      const n = Number(
        s.toString()?.replace(/[\s-]/g, ""),
      );

      if (!n || !checkLuhn(n)) {
        return err("Ej giltigt bankgiro-nummer");
      } else {
        return ok(s);
      }
    });
  }

  export function validateOrganisationsnummer(): Validator<string, string> {
    return new Validator((onr: string) => {
      const regex = /^\d{6}-\d{4}$/;
      if (!regex.test(onr)) {
        return err("Måste vara i formatet XXXXXX-XXXX");
      }

      const cleanOnr = onr.replace(/-/g, "");

      if (!isValidLuhn(cleanOnr)) {
        return err("Ej giltigt organisationsnummer");
      } else {
        return ok(onr);
      }
    });
  }

  export function validatePersonnummer(): Validator<string, string> {
    return new Validator((pnr: string) => {
      const regex =
        /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])[-+]?(\d{4})$/;
      if (!regex.test(pnr)) {
        return err("Måste vara i formatet ÅÅÅÅMMDD-XXXX");
      }

      const cleanPnr = pnr.replace(/\D/g, "");
      const yy = parseInt(cleanPnr.substr(cleanPnr.length - 10, 2));
      const mm = parseInt(cleanPnr.substr(cleanPnr.length - 8, 2));
      let dd = parseInt(cleanPnr.substr(cleanPnr.length - 6, 2));

      // Manage samordningsnummer
      const isSamordningsnummer = dd > 60;
      if (isSamordningsnummer) {
        dd -= 60;
      }

      if (!isValidDate(yy, mm, dd)) {
        return err("Felaktigt födelsedatum");
      }

      const luhnPart = isSamordningsnummer
        ? cleanPnr.substr(cleanPnr.length - 10, 4) +
          cleanPnr.substr(cleanPnr.length - 6)
        : cleanPnr.substr(-10);
      if (!isValidLuhn(luhnPart)) {
        return err("Ej giltigt personnummer");
      } else {
        return ok(pnr);
      }
    });
  }

  export function optional<output>(
    wrapped: Validator<string, output>,
  ): Validator<string, output | null> {
    return new Validator((s: string) => {
      return s === "" || s === null || s === undefined
        ? ok(null)
        : wrapped.fn(s);
    });
  }

  function isValidDate(yy: number, mm: number, dd: number): boolean {
    let year = yy;
    if (yy < 100) {
      year += yy > 20 ? 1900 : 2000;
    }
    const date = new Date(year, mm - 1, dd);
    return date.getFullYear() === year && date.getMonth() === mm - 1 &&
      date.getDate() === dd;
  }

  function isValidLuhn(number: string): boolean {
    let sum = 0;
    for (let i = 0; i < number.length; i++) {
      let digit = parseInt(number[i]);
      if ((number.length - i) % 2 === 0) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }
      sum += digit;
    }
    return sum % 10 === 0;
  }
}
